import React, { FC, useCallback, useEffect, useRef, useState } from "react";
// Importa anche CameraDevice per avere i tipi corretti dalla libreria
import { Html5Qrcode, CameraDevice } from "html5-qrcode";
import api from "../services/api";
import * as Tone from 'tone';
import { useConsent } from "../context/ConsentContext";
import { useAuth } from "../context/AuthContext";
import { Client } from "../context/DataContext";

type ScanStatus = 'idle' | 'success' | 'error';
interface ScanResult {
    status: ScanStatus;
    message: string;
}

interface CustomCameraDevice {
    id: string;
    label: string;
}


const ScannerOverlay: FC<{ status: ScanStatus; message: string }> = ({ status, message }) => {
    const baseClasses = "absolute inset-0 flex flex-col items-center justify-center p-4 transition-all duration-300 pointer-events-none";
    const statusClasses = {
        success: 'bg-green-900/90',
        error: 'bg-red-900/90',
        idle: 'bg-transparent'
    };

    const Icon = {
        success: (
            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-300 mb-4">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
        ),
        error: (
            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-300 mb-4">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
        ),
        idle: null,
    };

    return (
        <div className={`${baseClasses} ${statusClasses[status]}`}>
            {status === 'idle' ? (
                <div className="absolute inset-4 border-2 border-dashed border-slate-600 rounded-lg flex items-center justify-center">
                    <p className="text-white text-lg font-semibold bg-black/50 px-4 py-2 rounded-lg">{message}</p>
                </div>
            ) : (
                <>
                    {Icon[status]}
                    <p className="text-white text-2xl font-bold text-center">{message}</p>
                </>
            )}
        </div>
    );
};

/**
 * Mostra un messaggio chiaro quando l'accesso alla funzionalità è bloccato per mancanza di consenso.
 */
const ConsentBlockedView: FC = () => (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-gray-100 rounded-lg">
        <div className="max-w-md p-8 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Funzionalità Bloccata</h2>
            <p className="text-gray-700">
                Per utilizzare lo scanner, è necessario accettare i termini della nostra Privacy Policy riguardanti la raccolta dei dati.
            </p>
            <p className="mt-4 text-sm text-gray-500">
                Se hai rifiutato per errore, vai nella pagina del profilo gestire il consenso.
            </p>
        </div>
    </div>
);


const ClientScanPage: FC = () => {
    const { hasConsented } = useConsent();
    const [scanResult, setScanResult] = useState<{ status: any; message: string }>({ status: 'idle', message: 'Punta la fotocamera su un QR Code' });
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [cameras, setCameras] = useState<CameraDevice[]>([]);
    const [selectedCameraId, setSelectedCameraId] = useState<string | undefined>();
    const [all, setAll] = useState<string>("-1");
    const [today, setToday] = useState<string>("0");
    const [isMirrorMode, setIsMirrorMode] = useState<boolean>(false);
    const idClientRef = useRef<number>(-1);
    const [isOptionsVisible, setIsOptionsVisible] = useState(true);
    const [clients, setClients] = useState<Client[]>([]);
    const [isScannerReady, setIsScannerReady] = useState(false);
    const videoTrackRef = useRef<MediaStreamTrack | null>(null);
    const [zoomCapabilities, setZoomCapabilities] = useState<MediaTrackCapabilities['zoom'] | null>(null);
    const [zoomLevel, setZoomLevel] = useState<number>(1);
    const [manualInput, setManualInput] = useState<string>('');

    const scannerRef = useRef<Html5Qrcode | null>(null);
    const synthSuccessRef = useRef<Tone.Synth | null>(null);
    const synthErrorRef = useRef<Tone.Synth | null>(null);
    //const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
    const audioPlayerRef = useRef<Tone.Player | null>(null);

    const { user } = useAuth();
    const isPausedRef = useRef(false);
    const audioInitialized = useRef(false);
    const readerId = "qr-reader";


    const initializeZoomControls = () => {
        const videoElement = document.querySelector(`#${readerId} video`) as HTMLVideoElement;
        if (!videoElement || !videoElement.srcObject) return;

        const stream = videoElement.srcObject as MediaStream;
        const track = stream.getVideoTracks()[0];
        if (!track) return;

        const capabilities = track.getCapabilities();

        if (capabilities.zoom) {
            videoTrackRef.current = track;
            setZoomCapabilities(capabilities.zoom);
            setZoomLevel(capabilities.zoom.min);
        }
    };

    const handleManualSubmit = async () => {
        // Controlla che l'input non sia vuoto
        if (manualInput.trim() !== '') {
            try {
                // Invia solo il parametro "key" come parte dell'URL
                await api.get(`/api/scans/test2/${encodeURIComponent(manualInput)}`);

            } catch (error) {
                console.error("Errore durante il logging dell'evento:", error);
            }
            // Usa la stessa funzione della scansione per avere lo stesso comportamento
            //onScanSuccess(manualInput.trim());
            // Opzionale: pulisce la casella di testo dopo l'invio
            setManualInput('');
        }
    };

    const resetScans = async () => {
        const clientId = idClientRef.current
        const response = await api.get(`/api/scans/resetScans/${clientId}`);
        let result = response.data;
        if(result.status){
            setToday("0")
        }
    }


    const handleZoomChange = useCallback((newZoom: number) => {
        if (videoTrackRef.current && zoomCapabilities) {
            setZoomLevel(newZoom);

            videoTrackRef.current.applyConstraints({
                advanced: [{ zoom: newZoom }]
            }).catch(e => console.error("Errore nell'applicare lo zoom:", e));
        }
    }, [zoomCapabilities]);

    const checkData = useCallback(async (clientId: number) => {
        if(user && (user.client || (!user.client && clientId !== -1))){
            try {
                const response = await api.get(`/api/scans/dataToday/${clientId}`);
                let resultMessage = response.data.data;
                if(resultMessage.includes(";")){
                    const str = resultMessage.split(";")
                    setAll(str[1] || "0")
                    setToday(str[0] || "0")
                }
            } catch (err: any) {

            }
        }
    }, [user])

    const fetchClients = useCallback(async () => {
        try {
            const response = await api.get("/api/clients/getAllNoPage");
            if (response.data && response.data.status) {
                setClients(response.data.data);
            }
        } catch (err) {
            console.error("Errore nel recupero dei clienti:", err);
        }
    }, []);
    useEffect(() => { fetchClients(); checkData(-1); }, [user, fetchClients, checkData]);

    const onScanSuccess = useCallback(async (decodedText: string) => {
        if (isPausedRef.current) return;
        let checkTime = false;
        if (decodedText.toLowerCase().startsWith("http:") || decodedText.toLowerCase().startsWith("https:")) { return; }
        isPausedRef.current = true;
        if (!audioInitialized.current) { await Tone.start(); audioInitialized.current = true; }
        try {
            const response = await api.post(`/api/scans/scan/${decodedText}/${idClientRef.current}/-1`);
            let resultMessage = response.data.data;
            if (resultMessage.includes(";")) {
                const str = resultMessage.split(";");
                resultMessage = str[0]; setAll(str[2] || "0"); setToday(str[1] || "0");
            }
            if (response.data.status && resultMessage === "Valido") {
                setScanResult({ status: 'success', message: resultMessage });
                synthSuccessRef.current?.triggerAttackRelease("C5", "8n");
            } else if (response.data.status && resultMessage.toLowerCase().trim() === "valido-park") {
                setScanResult({ status: 'success', message: "Valido" });
                checkTime = true;
                synthSuccessRef.current?.triggerAttackRelease("C5", "8n");
            } else if (response.data.status && resultMessage === "Valido-Audio") {
                setScanResult({ status: 'success', message: "Valido" });
                //audioPlayerRef.current?.play().catch(e => console.error("Errore audio:", e));
                audioPlayerRef.current?.start();
            } else { throw new Error(resultMessage || 'QR Code non valido.'); }
        } catch (err: any) {
            // Usa il ref stabile per il suono di errore
            synthErrorRef.current?.triggerAttackRelease("E3", "8n");
            setScanResult({ status: 'error', message: err.response?.data?.message || err.message || "Errore sconosciuto." });
        }
        setTimeout(() => {
            setScanResult({ status: 'idle', message: 'Punta la fotocamera su un QR Code' });
            setTimeout(() => { isPausedRef.current = false; }, checkTime ? 2000 : 1200);
        }, checkTime ? 2500 : 1300);
    }, [setAll, setToday, setScanResult]);

    const onScanFailure = useCallback(() => { /* Nessuna azione necessaria */ }, []);

    // --- useEffect N.1: Inizializzazione Globale e Cleanup Finale ---
    useEffect(() => {
        if (!hasConsented) return;

        // 1. Crea l'istanza dello scanner UNA SOLA VOLTA
        scannerRef.current = new Html5Qrcode(readerId, { verbose: false });

        // 2. Inizializza TUTTI gli oggetti audio UNA SOLA VOLTA
        synthSuccessRef.current = new Tone.Synth().toDestination();
        synthErrorRef.current = new Tone.Synth({ oscillator: { type: 'sawtooth' } }).toDestination();
        //audioPlayerRef.current = new Audio('/appetito.mp3');
        //audioPlayerRef.current.preload = 'auto';
        audioPlayerRef.current = new Tone.Player('/appetito.mp3').toDestination();


        // 3. Ottieni la lista delle fotocamere per il menu
        Html5Qrcode.getCameras()
            .then(devices => { if (devices?.length) { setCameras(devices); } })
            .catch(err => console.error("Impossibile enumerare le fotocamere:", err));

        setIsScannerReady(true);

        // 4. Funzione di Cleanup: viene eseguita solo allo smontaggio del componente
        return () => {
            const scanner = scannerRef.current;
            if (scanner?.isScanning) {
                scanner.stop().catch(err => console.error("Errore nel fermare lo scanner durante lo smontaggio:", err));
            }
        };
    }, [hasConsented]); // Dipende solo dal consenso, eseguito una volta.

    // --- useEffect N.2: Gestione Avvio/Cambio Fotocamera (VERSIONE CORRETTA E SEMPLIFICATA) ---
    useEffect(() => {
        if (!isScannerReady || !hasConsented) return;
        const scanner = scannerRef.current;
        if (!scanner) return;

        // Resetta lo stato dello zoom quando la fotocamera cambia
        videoTrackRef.current = null;
        setZoomCapabilities(null);

        // Funzione per fermare lo scanner se è attivo
        const stopScanner = () => scanner.isScanning ? scanner.stop() : Promise.resolve();

        stopScanner()
            .catch(() => { /* Ignora l'errore se non stava scansionando */ })
            .finally(() => {
                setCameraError(null);

                // Configurazione flessibile ma con qrbox per una migliore scansione
                const config = { fps: 30, /*qrbox: { width: 250, height: 250 }*/ };

                const handleSuccess = () => {
                    // Inizializza lo zoom solo dopo che la fotocamera è partita
                    setTimeout(() => initializeZoomControls(), 500);
                };

                if (selectedCameraId) {
                    scanner.start(selectedCameraId, config, onScanSuccess, onScanFailure)
                        .then(handleSuccess)
                        .catch(() => setCameraError("Impossibile avviare la fotocamera selezionata."));
                } else {
                    scanner.start({ facingMode: 'environment' }, config, onScanSuccess, onScanFailure)
                        .then(handleSuccess)
                        .catch(() => {
                            // Fallback sulla prima fotocamera disponibile se quella posteriore non funziona
                            if (cameras.length > 0) {
                                scanner.start(cameras[0].id, config, onScanSuccess, onScanFailure)
                                    .then(handleSuccess)
                                    .catch(() => setCameraError("Impossibile avviare qualsiasi fotocamera."));
                            } else {
                                setCameraError("Nessuna fotocamera trovata. Controlla i permessi.");
                            }
                        });
                }
            });

    }, [selectedCameraId, hasConsented, cameras, isScannerReady, onScanSuccess, onScanFailure]);

    const handleChangeClient = (value: string) => {
        const newId = Number(value);
        idClientRef.current = newId;
        checkData(newId);
    };

    /*useEffect(() => {
        let buffer = "";
        let lastTime = Date.now();

        const handleKeyDown = async (e: KeyboardEvent) => {
            //console.log(e)
            const now = Date.now();
            const diff = now - lastTime;
            if(diff > 2000)
                buffer = ""
            lastTime = now;

            //if (!isFromScanner) return;
            console.log(buffer)
            if (e.key === "Enter") {
                if (buffer.length > 0) {
                    onScanSuccess(buffer);
                    buffer = "";
                }
            } else if (e.key.length === 1) {
                buffer += e.key;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onScanSuccess]);*/


    useEffect(() => {
        let buffer = "";
        let lastTime = 0; // Inizializza a 0 per la prima esecuzione

        const handleKeyDown = (e: KeyboardEvent) => {
            if(isPausedRef.current) return;

            const now = Date.now();
            const diff = now - lastTime;
            lastTime = now;
            console.log('lastTime', lastTime);
            console.log(buffer)
            // Se passano più di 300ms, è una nuova scansione.
            // Resetta il buffer PRIMA di aggiungere il carattere.
            if (diff > 280) {
                buffer = "";
            }
            console.log("final: " + buffer)
            if (e.key === "Enter") {
                if (buffer.length > 3 /*&& buffer.startsWith("code_")*/) {
                    onScanSuccess(buffer);
                }
                buffer = "";
            } else if (e.key.length === 1) {
                buffer += e.key;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onScanSuccess]);

    if (!hasConsented) { return <ConsentBlockedView />; }

    return (
        <div>
            <h1 className="text-3xl font-bold text-white mb-6">Scansiona QR Code</h1>
            <div className="max-w-2xl mx-auto mb-4 flex flex-col justify-between items-center">
                <button
                    onClick={() => setIsOptionsVisible(!isOptionsVisible)}
                    className="flex items-center space-x-2 text-white p-2 rounded-md hover:bg-slate-700/50 transition-colors"
                >
                    <span>Opzioni</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-5 w-5 transition-transform duration-300 ${isOptionsVisible ? 'rotate-180' : ''}`}
                        viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>

                <div className={`w-full overflow-hidden transition-all duration-300 ${isOptionsVisible ? 'max-h-96 mt-4' : 'max-h-0'}`}>
                    {user && !user.client &&
                        <div className="flex items-center space-x-2 mb-4">
                            <label className="text-white">Seleziona Cliente:</label>
                            <select
                                className="p-2 rounded-md bg-slate-700 text-white cursor-pointer w-full"
                                defaultValue={idClientRef.current}
                                onChange={(e) => handleChangeClient(e.target.value)}
                            >
                                <option value="-1">Tutti i clienti</option>
                                {clients.map(client => (
                                    <option key={client.id} value={client.id.toString()}>
                                        {client.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    }

                    <div className="flex items-center space-x-2 mb-4">
                        <label className="text-white">Fotocamera:</label>
                        <select
                            className="p-2 rounded-md bg-slate-700 text-white cursor-pointer w-full"
                            value={selectedCameraId || ''}
                            onChange={(e) => setSelectedCameraId(e.target.value)}
                            disabled={cameras.length === 0}
                        >
                            <option value="">Default (Posteriore)</option>
                            {cameras.map(camera => (
                                <option key={camera.id} value={camera.id}>
                                    {camera.label || `Fotocamera ${camera.id}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center space-x-2">
                        <label className="text-white cursor-pointer" htmlFor="mirror-mode">Modalità Specchio</label>
                        <input
                            type="checkbox"
                            id="mirror-mode"
                            checked={isMirrorMode}
                            onChange={(e) => setIsMirrorMode(e.target.checked)}
                            className="h-4 w-4 rounded-md text-slate-500 cursor-pointer"
                        />
                    </div>

                    {zoomCapabilities && (
                        <div className="flex items-center space-x-3 mt-4">
                            <label className="text-white">Zoom:</label>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>
                            <input
                                type="range"
                                min={zoomCapabilities.min}
                                max={zoomCapabilities.max}
                                step={zoomCapabilities.step}
                                value={zoomLevel}
                                onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="max-w-2xl mx-auto bg-slate-800/50 border border-slate-700 p-4 rounded-lg text-center relative overflow-hidden aspect-square">
                {cameraError ? (
                    <div className="flex items-center justify-center h-full text-red-400 font-semibold">{cameraError}</div>
                ) : (
                    <>
                        <div id={readerId} className={`w-full h-full ${isMirrorMode ? 'transform scale-x-[-1]' : ''}`} />
                        <ScannerOverlay status={scanResult.status} message={scanResult.message} />
                    </>
                )}
            </div>

            {/*<div className="max-w-2xl mx-auto mt-4 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
                <label htmlFor="manual-code" className="block text-white text-sm font-bold mb-2">
                    O inserisci il codice manualmente:
                </label>
                <div className="flex space-x-2">
                    <input
                        id="manual-code"
                        type="text"
                        value={manualInput}
                        onChange={(e) => setManualInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleManualSubmit(); }}
                        placeholder="Digita il codice e premi Invio"
                        className="flex-grow p-2 rounded-md bg-slate-700 text-white"
                    />
                    <button
                        onClick={handleManualSubmit}
                        className="p-2 bg-slate-600 text-white rounded-md hover:bg-slate-500 transition-colors"
                    >
                        Invia
                    </button>
                </div>
            </div>*/}

            {user && (user.client || (!user.client && idClientRef.current !== -1)) && all !== "-1" &&
                <><div className="max-w-2xl mx-auto mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
                        <p className="text-slate-400 text-sm">Scansioni Valide Oggi</p>
                        <p className="text-white text-3xl font-bold mt-1">{today}</p>
                    </div>
                    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
                        <p className="text-slate-400 text-sm">Scansioni Valide Totali</p>
                        <p className="text-white text-3xl font-bold mt-1">{all}</p>
                    </div>
                </div>
                    <br/>
                    <div className="flex justify-center">

                        <button onClick={() => resetScans()} className="px-5 py-2.5 rounded-lg text-white font-semibold bg-sky-600 hover:bg-sky-500 transition-colors flex items-center space-x-2">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                            <span>Reset Scans</span>
                        </button>

                    </div>
        </>
            }
        </div>
    );
};

export default ClientScanPage;